import { useEffect, useState } from "react";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { getAuditLog, IAuditLog, ILog } from "./AuditLogViewController";
import UsePaginationHook from "../../hooks/UsePaginationHook";
import { IPagination } from "../../data/AppInterface";
import {
  FormRightModalWrapper,
  ICImage,
  Loader,
} from "../../core-component/ui";
import {
  dateFormat,
  getLocalDate,
  getTextContentOnly,
  limitString,
  prepareMessageFromParams,
  timeFormat,
} from "../../utils/AppFunctions";
import {
  arrowDownIcon,
  closeIcon,
  infoIcon,
  rightArrowIcon,
} from "../../assets/icons/SvgIconList";
import { AUDIT_ACTION } from "../../utils/AppEnumerations";
import { cn } from "../../lib";

interface IAuditLogProps {
  handleClose: (status: boolean) => void;
  auditLogRecordId: string;
  auditLogType: string;
  titleName: string;
}

const AuditLog: React.FC<IAuditLogProps> = ({
  handleClose,
  auditLogRecordId,
  auditLogType,
  titleName,
}) => {
  const { t } = UseTranslationHook();
  const [loading, setLoading] = useState(false);
  const [auditLog, setAuditLog] = useState<IAuditLog[]>([]);
  const { pagination, setChangePagination } = UsePaginationHook();
  const [readMore, setReadMore] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isShowModal, setIsShowModal] = useState(false);
  const [selectShow, setSelectShow] = useState<ILog[]>([]);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    fetchAuditLog(pagination);
    setIsVisible(true);
  }, []);

  const fetchAuditLog = async (paginationPayload: IPagination) => {
    setLoading(true);
    try {
      await getAuditLog(
        handleClose,
        (newLogs) => {
          if (Array.isArray(newLogs)) {
            setAuditLog((prevLogs) => [...prevLogs, ...newLogs]);
          }
        },
        paginationPayload,
        setChangePagination,
        auditLogRecordId,
        auditLogType,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleMore = () => {
    if (pagination.currentPage < pagination.totalPages) {
      setIsLoadingMore(true);
      fetchAuditLog({
        ...pagination,
        currentPage: pagination.currentPage + 1,
      }).finally(() => setIsLoadingMore(false));
    }
  };

  const getInitials = (name: string) =>
    name
      .split(" ")
      .map((n) => n.charAt(0).toUpperCase())
      .join("")
      .slice(0, 2);

  const stringToColor = (inputString: string) => {
    let hash = 0;
    const normalizedString = inputString.trim().toLowerCase();

    for (let i = 0; i < normalizedString.length; i++) {
      hash = normalizedString.charCodeAt(i) + ((hash << 5) - hash);
      hash = hash & hash;
    }

    const hue = ((hash % 360) + 360) % 360;
    return `hsl(${hue}, 70%, 50%)`;
  };
  const getRoleAction = (role: ILog) => {
    if (role.old_value === null) {
      return "Create";
    }
    return "Update";
  };

  const renderActionDetails = (role: ILog, index: number) => (
    <div className="py-2">
      <div className="flex mb-1">
        <div className="flex items-center">
          {
            AUDIT_ACTION[
              role.new_value === null ? "Delete" : getRoleAction(role)
            ]
          }{" "}
          {role.field_name}
        </div>
        {(role.new_value?.length > 20 || role.old_value?.length > 20) && (
          <div
            className="flex items-center text-accent hover:underline ml-2"
            onClick={() => {
              setSelectShow([role]);
              setIsShowModal(true);
            }}
          >
            <div className="size-5 cursor-pointer">{infoIcon}</div>
          </div>
        )}
      </div>
      <div className="flex items-center justify-start">
        <span className="text-muted ">
          {limitString(getTextContentOnly(role.old_value || ""), 20)}
        </span>
        {role.old_value && role.new_value ? (
          <>
            <span className="mx-1 size-5 text-default shrink-0 ">
              {rightArrowIcon}
            </span>
            <span className="text-muted ">
              {limitString(getTextContentOnly(role.new_value), 20)}
            </span>
          </>
        ) : (
          <span className="text-muted">
            {limitString(getTextContentOnly(role.new_value), 20)}
          </span>
        )}
      </div>
    </div>
  );

  return (
    <>
      {loading && !isLoadingMore ? (
        <Loader />
      ) : (
        <FormRightModalWrapper
          containerClassName="!w-128"
          onClose={() => handleClose(false)}
          title={titleName}
        >
          {auditLog.length > 0 ? (
            <>
              {auditLog.map((item) => (
                <div key={item.id} className="bg-accent rounded-lg ">
                  <div className="flex">
                    <div className="relative flex items-center justify-center size-11 rounded-full overflow-hidden">
                      {item.profile_media_url ? (
                        <ICImage
                          className="h-full w-full object-cover rounded-full"
                          imageUrl={item.profile_media_url}
                        />
                      ) : (
                        <div
                          className="flex items-center justify-center h-full w-full rounded-full text-on-accent font-bold"
                          style={{
                            backgroundColor: stringToColor(item.action_by),
                          }}
                        >
                          {getInitials(item.action_by)}
                        </div>
                      )}
                    </div>
                    <div className="ml-3">
                      <div className="text-sm text-muted">
                        {prepareMessageFromParams(t("action_date_at_time"), [
                          ["actionDate", dateFormat(item.action_date)],
                          [
                            "actionTime",
                            timeFormat(getLocalDate(item.action_date)),
                          ],
                        ])}
                      </div>
                      <div className="font-normal">
                        {prepareMessageFromParams(t("action_by_name"), [
                          ["action", item.action],
                          ["actionName", item.action_by],
                        ])}
                      </div>
                    </div>
                  </div>
                  <div className="ml-5 my-1 pl-5 py-2 border-l-2 border-muted">
                    {item.action === AUDIT_ACTION.Update && item.log && (
                      <div className="pl-4">
                        {(readMore ? item.log : item.log.slice(0, 2)).map(
                          renderActionDetails,
                        )}
                        {item.log.length > 2 && (
                          <button
                            onClick={() => setReadMore(!readMore)}
                            className="flex justify-end pb-2 text-accent hover:underline"
                          >
                            {readMore ? t("read_less") : t("read_more")}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              <div className="flex justify-center">
                {pagination.currentPage < pagination.totalPages && (
                  <div
                    className="animate-bounce cursor-pointer size-10 mt-4 flex items-center justify-center bg-accent-emphasis rounded-full text-on-accent"
                    onClick={handleMore}
                  >
                    {isLoadingMore ? (
                      t("loading")
                    ) : (
                      <div className="size-5">{arrowDownIcon}</div>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex h-full items-center justify-center text-muted">
              {t("no_history_found")}
            </div>
          )}
        </FormRightModalWrapper>
      )}
      {isShowModal &&
        selectShow.length > 0 &&
        selectShow.map((select, index) => (
          <div
            key={index}
            className={cn(
              `fixed inset-0 z-50 backdrop-blur-sm flex items-center justify-center bg-black bg-opacity-40 transition-opacity duration-300`,
              { "opacity-100": isVisible, "opacity-0": !isVisible },
            )}
          >
            <div
              className={cn(
                `bg-subtle rounded-lg shadow-lg max-w-3xl w-full mx-4 md:mx-0 custom-transform `,
              )}
            >
              <div className="flex justify-between items-center p-4 border-b">
                <h2 className="font-semibold">{select.field_name}</h2>
                <div className="size-5 cursor-pointer">
                  <div
                    onClick={() => {
                      setIsShowModal(false);
                    }}
                  >
                    {closeIcon}
                  </div>
                </div>
              </div>
              <div className="p-4 grid grid-flow-col items-center">
                {select.old_value && (
                  <div className="max-h-full overflow-hidden flex flex-col ">
                    {select.old_value}
                  </div>
                )}
                {select.old_value && select.new_value && (
                  <span className="mx-1 size-5 text-default shrink-0 mr-4">
                    {rightArrowIcon}
                  </span>
                )}
                {select.new_value && (
                  <div className="max-h-full overflow-hidden flex flex-col">
                    {select.new_value}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
    </>
  );
};

export default AuditLog;
